import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Auth from "src/services/auth";
import useQueryParams from "src/hooks/use-query-params";
import { ToastContext } from "src/components/providers/toast-provider";
import { LoadingContext } from "./loading-provider";

const AuthContext = React.createContext();
AuthContext.displayName = "AuthContext";

const AuthProvider = (props) => {
    const { addToLoading, removeFromLoading } = useContext(LoadingContext);
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [authStarted, setAuthStarted] = useState(false);
    const [auth, setAuth] = useState({
        isLoading: true,
        isError: false,
        isDemo: false,
        user: null,
        jwt: null,
        idToken: null,
    });

    const query = useQueryParams();
    const { pathname } = window.location;
    const navigate = useNavigate();

    useEffect(() => {
        if (authStarted) {
            if (!auth.isLoading) Auth.checkAuthenticated();
            return;
        }

        addToLoading("auth");

        // eslint-disable-next-line consistent-return
        const authenticate = async () => {
            setAuthStarted(true);

            const code = query.get("code");
            const error = query.get("error");
            const adminConsent = query.get("admin_consent");
            if (adminConsent) {
                if (!error) {
                    setMessage("Microsoft Azure Active Directory Connection Consent Registered.");
                    setSeverity("success");
                    setIsToastTriggered(true);
                } else {
                    setMessage(`Microsoft Azure Active Directory Connection Error: ${error}`);
                    setSeverity("error");
                    setIsToastTriggered(true);
                }
            }

            if (
                !adminConsent &&
                error &&
                error === "access_denied" &&
                pathname !== "/integrations/slack/oauth"
            )
                return navigate(`/error?${query.toString()}`);

            try {
                const expired = Auth.isTokenExpired();
                if (pathname.startsWith("/email-validation")) {
                    if (Auth.idToken.loginMethodVerified) {
                        await Auth.checkAuthenticated();
                    } else {
                        await Auth.checkAuthenticated();
                        await Auth.refreshToken();
                    }
                    navigate("/dashboard");
                } else if (
                    pathname !== "/integrations/gitlab/oauth" &&
                    pathname !== "/integrations/slack/oauth" &&
                    pathname !== "/integrations/github/oauth" &&
                    pathname !== "/integrations/github-app/oauth" &&
                    pathname !== "/integrations/google/oauth" &&
                    pathname !== "/integrations/jira/oauth" &&
                    pathname !== "/integrations/notion/oauth" &&
                    pathname !== "/integrations/microsoft/oauth" &&
                    pathname !== "/integrations/azure/oauth" &&
                    query.has("code")
                ) {
                    query.delete("code");
                    navigate({
                        search: query.toString(),
                    });
                    await Auth.authenticate(code);
                } else if (expired) {
                    await Auth.refreshToken();
                } else {
                    setAuth((prevState) => ({
                        ...prevState,
                        jwt: Auth.jwt,
                        idToken: Auth.idToken,
                    }));
                    await Auth.checkAuthenticated();
                }

                setAuth({
                    isLoading: false,
                    isError: false,
                    isDemo: Auth.idToken && Auth.idToken.isDemo,
                    jwt: Auth.jwt,
                    idToken: Auth.idToken,
                    user: {
                        ...Auth.user,
                        ...(Auth.user &&
                            Auth.user.picture && {
                                picture: `${Auth.user.picture}?${new Date().getTime()}`,
                            }),
                    },
                });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);

                setAuth({
                    isLoading: false,
                    isError: true,
                    isDemo: false,
                    jwt: null,
                    idToken: null,
                    user: null,
                });
            }
            removeFromLoading("auth");
            // if (pathname.startsWith("/email-validation")) {
            //     navigate("/dashboard");
            // }
        };

        authenticate();
    }, [authStarted, navigate, query]);

    const isProfileComplete = auth.user
        ? auth.user.jobTitle !== undefined &&
          auth.user.jobTitle !== null &&
          auth.user.jobTitle !== "" &&
          auth.user.displayName !== undefined &&
          auth.user.displayName !== null &&
          auth.user.displayName !== ""
        : false;

    const isUserPayingCustomer = !!(
        auth.user &&
        auth.user.plans &&
        auth.user.plans.length > 0 &&
        !!auth.user.plans.find(
            (plan) => plan.status === "active" && !plan.type.toLowerCase().includes("free")
        )
    );

    const teamSubscription =
        auth.user &&
        auth.user.plans &&
        auth.user.plans.length > 0 &&
        auth.user.plans.find(
            (plan) =>
                plan.status === "active" &&
                (plan.type.toLowerCase().includes("team") ||
                    plan.type.toLowerCase().includes("enterprise"))
        );

    const enterpriseSubscription =
        auth.user &&
        auth.user.plans &&
        auth.user.plans.length > 0 &&
        auth.user.plans.find(
            (plan) =>
                plan.status === "active" &&
                (plan.type.toLowerCase().includes("team") ||
                    plan.type.toLowerCase().includes("enterprise"))
        );

    const userHasTeamsSubscription = !!teamSubscription;

    const userHasEnterpriseSubscription = !!enterpriseSubscription;

    const isTeamSubscriptionAdmin = teamSubscription && !!teamSubscription.isAdmin;

    const updateUser = async () => {
        const updatedUser = await Auth.updateUser();
        if (updatedUser.picture)
            updatedUser.picture = `${updatedUser.picture}?${new Date().getTime()}`;
        setAuth({ ...auth, user: updatedUser });
    };

    const value = React.useMemo(
        () => ({
            ...auth,
            authStarted,
            isProfileComplete,
            isUserPayingCustomer,
            isTeamSubscriptionAdmin,
            userHasTeamsSubscription,
            userHasEnterpriseSubscription,
            updateUser,
        }),
        [
            auth,
            authStarted,
            isProfileComplete,
            isUserPayingCustomer,
            userHasTeamsSubscription,
            userHasEnterpriseSubscription,
            isTeamSubscriptionAdmin,
            updateUser,
        ]
    );

    return <AuthContext.Provider value={value} {...props} />;
};

const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (context === undefined) {
        throw new Error(`useAuth must be used within a AuthProvider`);
    }
    return context;
};

export { AuthProvider, useAuth };
